import React, { useState, useRef, useEffect, useCallback } from "react";
import SignatureCanvas from "react-signature-canvas";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

// Google Maps Geocoding key. Restrict this in Google Cloud Console to
// the production domain + localhost — keys in client code are visible.
const GOOGLE_GEOCODE_KEY = "AIzaSyAA5mCXU8WrBOym-1rZvzADu4YvZWhAja8";

const NAME_REGEX = /^[A-Za-z\s.'-]{2,50}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^[0-9]{10}$/;
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const validators = {
  fullName: (value) => {
    if (!value.trim()) return "Full name is required.";
    if (!NAME_REGEX.test(value.trim())) return "Enter a valid name (letters only, 2-50 characters).";
    return "";
  },
  email: (value) => {
    if (!value.trim()) return "Email address is required.";
    if (!EMAIL_REGEX.test(value.trim())) return "Enter a valid email address.";
    return "";
  },
  mobile: (value) => {
    if (!value.trim()) return "Mobile number is required.";
    if (!MOBILE_REGEX.test(value.trim())) return "Enter a valid 10-digit mobile number.";
    return "";
  },
  panCard: (value) => {
    if (!value.trim()) return "PAN card number is required.";
    if (!PAN_REGEX.test(value.trim())) return "Enter a valid PAN (e.g. ABCDE1234F).";
    return "";
  },
};

export default function NewKyc() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const sigPadRef = useRef(null);
  const [signature, setSignature] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [signatureError, setSignatureError] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mobile, setMobile] = useState("");
  const [panCard, setPanCard] = useState("");
  const [panCardImage, setPanCardImage] = useState(null);
  const [panCardImagePreview, setPanCardImagePreview] = useState(null);
  const [panCardImageError, setPanCardImageError] = useState(null);

  // Field-level validation state
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Geolocation — captured for the regulatory audit trail.
  // status: idle → asking → granted | denied | unavailable | error | skipped
  const [coords, setCoords] = useState(null);
  const [address, setAddress] = useState("");
  const [geoStatus, setGeoStatus] = useState("idle");
  const [geoError, setGeoError] = useState("");

  const getAddress = useCallback(async (lat, lng) => {
    try {
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_GEOCODE_KEY}`
      );
      const data = await res.json();

      if (data.results && data.results.length > 0) {
        const result = data.results[0];

        let area = "";
        let city = "";
        let state = "";
        let country = "";
        let pincode = "";

        result.address_components.forEach((comp) => {
          const types = comp.types;
          if (types.includes("sublocality") || types.includes("neighborhood")) area = comp.long_name;
          if (types.includes("locality")) city = comp.long_name;
          if (types.includes("administrative_area_level_1")) state = comp.long_name;
          if (types.includes("country")) country = comp.long_name;
          if (types.includes("postal_code")) pincode = comp.long_name;
        });

        const parts = [area, city, state, country].filter(Boolean).join(", ");
        setAddress(pincode ? `${parts} - ${pincode}` : parts);
      }
    } catch (err) {
      console.error("Geocode Error:", err);
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      setGeoStatus("unavailable");
      setGeoError("Your browser does not support location services.");
      return;
    }

    setGeoStatus("asking");
    setGeoError("");

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setCoords({ lat, lng });
        setGeoStatus("granted");
        getAddress(lat, lng);
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGeoStatus("denied");
          setGeoError(
            "Location access was blocked. Please allow location in your browser settings to continue."
          );
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          setGeoStatus("error");
          setGeoError("We couldn't determine your location. Please try again.");
        } else if (err.code === err.TIMEOUT) {
          setGeoStatus("error");
          setGeoError("Location request timed out. Please try again.");
        } else {
          setGeoStatus("error");
          setGeoError("Something went wrong while fetching your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, [getAddress]);

  // Ask on mount. The popup handles denied/error states with a retry button.
  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  const validateField = (id, value) => (validators[id] ? validators[id](value) : "");

  const fieldSetters = { fullName: setFullName, email: setEmail, mobile: setMobile, panCard: setPanCard };

  const handleFieldChange = (id, rawValue) => {
    let value = rawValue;

    if (id === "mobile") {
      value = value.replace(/\D/g, "").slice(0, 10);
    }
    if (id === "panCard") {
      value = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10);
    }

    fieldSetters[id](value);

    if (touched[id]) {
      setErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
    }
  };

  const handleFieldBlur = (id, value) => {
    setTouched((prev) => ({ ...prev, [id]: true }));
    setErrors((prev) => ({ ...prev, [id]: validateField(id, value) }));
  };

  const fieldStateClass = (id) => {
    if (!touched[id]) {
      return "border-[#1e6091]/30 focus:border-[#1e6091] focus:ring-[#1e6091]/20";
    }
    if (errors[id]) {
      return "border-rose-400 focus:border-rose-500 focus:ring-rose-200";
    }
    return "border-emerald-400 focus:border-emerald-500 focus:ring-emerald-200";
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
    setSignature(null);
    setIsSaved(false);
    setSignatureError("");
  };

  const saveSignature = () => {
    if (sigPadRef.current && !sigPadRef.current.isEmpty()) {
      const data = sigPadRef.current.toDataURL();
      setSignature(data);
      setIsSaved(true);
      setSignatureError("");
    } else {
      setSignatureError("Please sign before saving.");
    }
  };

  const handlePanCardImageChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;

    if (!["image/png", "image/jpeg", "application/pdf"].includes(file.type)) {
      setPanCardImageError("Only PNG, JPG or PDF files are allowed.");
      setPanCardImage(null);
      setPanCardImagePreview(null);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setPanCardImageError("File too large (max 5MB)");
      setPanCardImage(null);
      setPanCardImagePreview(null);
      return;
    }

    setPanCardImageError(null);
    setPanCardImage(file);

    const reader = new FileReader();
    reader.onload = () => {
      setPanCardImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    const newErrors = {
      fullName: validateField("fullName", fullName),
      email: validateField("email", email),
      mobile: validateField("mobile", mobile),
      panCard: validateField("panCard", panCard),
    };
    setErrors(newErrors);
    setTouched({ fullName: true, email: true, mobile: true, panCard: true });

    let hasBlockingError = Object.values(newErrors).some(Boolean);

    if (!panCardImage || !panCardImagePreview) {
      setPanCardImageError("PAN card image is required.");
      hasBlockingError = true;
    }

    if (!signature) {
      setSignatureError("Signature is required.");
      hasBlockingError = true;
    }

    if (hasBlockingError) return;

    setLoading(true);

    const payload = {
      fullName,
      email,
      mobile,
      panCard,
      panCardImageBase64: panCardImagePreview,
      signatureBase64: signature,
    };

    // Location is optional at the backend (falls back to IP), but include it
    // when the user has granted permission — it goes into the audit trail.
    if (coords) {
      payload.location = address || `Lat ${coords.lat}, Lng ${coords.lng}`;
      payload.lat = String(coords.lat);
      payload.lng = String(coords.lng);
    }

    try {
      const res = await fetch(
        "https://api.optionbricks.in/api/submitandpay",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (res.ok) {
        sessionStorage.setItem(
          "submissionData",
          JSON.stringify({ fullName, email, mobile, panCard })
        );
        navigate("/thank-you");
      } else {
        alert(data.message || "Failed");
      }
    } catch (error) {
      console.error("API Error:", error);
      alert("Server Error");
    } finally {
      setLoading(false);
    }
  };

  // Live completion tracker — drives the progress bar and the step rail.
  const steps = [
    {
      key: "identity",
      label: "Personal Information",
      icon: "fa-user",
      done: !!(fullName && email && mobile && !validators.fullName(fullName) && !validators.email(email) && !validators.mobile(mobile)),
    },
    { key: "document", label: "Identity Document", icon: "fa-id-card", done: !!(panCard && !validators.panCard(panCard) && panCardImage) },
    { key: "signature", label: "E-Signature", icon: "fa-pen-nib", done: !!signature },
  ];
  const completedSteps = steps.filter((s) => s.done).length;
  const progress = Math.round((completedSteps / steps.length) * 100);
  const canSubmit =
    fullName &&
    email &&
    mobile &&
    panCard &&
    panCardImage &&
    signature &&
    !validators.fullName(fullName) &&
    !validators.email(email) &&
    !validators.mobile(mobile) &&
    !validators.panCard(panCard) &&
    !loading;

  // Decorative market ticker — purely visual, sets the trading-floor tone.
  const ticker = [
    { sym: "NIFTY 50", val: "23,567.10", chg: "+0.84%", up: true },
    { sym: "SENSEX", val: "77,209.90", chg: "+0.71%", up: true },
    { sym: "BANKNIFTY", val: "51,402.55", chg: "-0.23%", up: false },
    { sym: "RELIANCE", val: "2,945.30", chg: "+1.12%", up: true },
    { sym: "TCS", val: "4,102.85", chg: "-0.41%", up: false },
    { sym: "INFY", val: "1,678.20", chg: "+0.55%", up: true },
    { sym: "USD/INR", val: "83.42", chg: "+0.08%", up: true },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-700 font-sans flex flex-col">


  <Helmet>
  <title>User Consent | Option Bricks</title>
  <meta
    name="description"
    content="Complete your KYC verification with Option Bricks to securely access our research services and comply with regulatory requirements. Fast, secure, and hassle-free verification process."
  />
</Helmet>

      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"
      />

      <style>{`
        @keyframes tickerScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .ticker-track { animation: tickerScroll 32s linear infinite; }
      `}</style>

      {/* Location permission popup */}
      {(geoStatus === "denied" || geoStatus === "error" || geoStatus === "unavailable") && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-[#1e6091]/30 max-w-md w-full rounded-2xl p-7 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-rose-500/15 text-rose-400">
                <i className="fa fa-map-marker-alt text-xl"></i>
              </span>
              <h4 className="text-lg font-bold text-slate-900">Location Access Required</h4>
            </div>
            <p className="text-sm text-slate-600 mb-3">{geoError}</p>
            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
              We capture your location only to record where the agreement was
              signed — it's part of the regulatory audit trail. Open your
              browser's site settings to allow location for this site, then tap Retry.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setGeoStatus("skipped")}
                className="px-4 py-2.5 text-sm font-medium rounded-lg border border-[#1e6091]/40 text-slate-600 hover:bg-gray-100 transition-colors"
              >
                Continue without
              </button>
              <button
                type="button"
                onClick={requestLocation}
                className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-[#1e6091] text-white hover:bg-[#185076] transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Top ticker bar ── */}
      {/* <div className="border-b border-[#1e6091]/30 bg-gray-50 overflow-hidden">
        <div className="ticker-track flex w-max gap-8 py-2 px-4">
          {[...ticker, ...ticker].map((t, i) => (
            <div key={i} className="flex items-center gap-2 whitespace-nowrap text-xs">
              <span className="font-bold text-slate-600">{t.sym}</span>
              <span className="text-slate-500">{t.val}</span>
              <span className={`font-semibold ${t.up ? "text-[#1e6091]" : "text-rose-400"}`}>
                <i className={`fa fa-caret-${t.up ? "up" : "down"} mr-0.5`}></i>{t.chg}
              </span>
            </div>
          ))}
        </div>
      </div> */}

      {/* ── Header ── */}
      <header className="border-b border-[#1e6091]/30 bg-white/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#1e6091] text-white">
              <i className="fa fa-chart-line"></i>
            </span>
            <div className="leading-tight">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight">Optionbricks</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#1e6091]">Trading Terminal</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-slate-500">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#1e6091] opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1e6091]"></span>
            </span>
            Markets Open · NSE / BSE
          </div>
        </div>
      </header>

      {/* ── Main ── */}
      <main className="flex-1 w-full">
        <div className="relative">
          <div className="max-w-6xl mx-auto px-5 py-8 md:py-10">
            {/* Title row */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-7">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#1e6091] mb-1.5">
                  Account Onboarding
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
                  KYC Verification
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Complete your verification to activate trading access.
                </p>
              </div>

              {/* Location status pill */}
              <div>
                {geoStatus === "asking" && (
                  <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 border border-[#1e6091]/30 text-slate-500">
                    <i className="fa fa-spinner fa-spin"></i> Fetching location…
                  </span>
                )}
                {geoStatus === "granted" && (
                  <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-[#1e6091]/10 border border-[#1e6091]/30 text-[#1e6091] max-w-xs">
                    <i className="fa fa-location-dot shrink-0"></i>
                    <span className="truncate">Verified{address ? ` · ${address}` : ""}</span>
                  </span>
                )}
                {geoStatus === "skipped" && (
                  <button
                    type="button"
                    onClick={requestLocation}
                    className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 hover:bg-amber-500/20 transition-colors"
                  >
                    <i className="fa fa-triangle-exclamation"></i> Location skipped — enable
                  </button>
                )}
              </div>
            </div>

            {/* Horizontal stepper */}
            <div className="mb-8">
              <div className="flex items-center">
                {steps.map((s, i) => (
                  <React.Fragment key={s.key}>
                    <div className="flex items-center gap-2.5 shrink-0">
                      <span
                        className={`grid place-items-center w-9 h-9 rounded-full text-sm border transition-all ${
                          s.done
                            ? "bg-[#1e6091] border-[#1e6091] text-white"
                            : "bg-gray-100 border-[#1e6091]/40 text-slate-500"
                        }`}
                      >
                        <i className={`fa ${s.done ? "fa-check" : s.icon}`}></i>
                      </span>
                      <div className="hidden sm:block leading-tight">
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Step {i + 1}</p>
                        <p className={`text-xs font-semibold ${s.done ? "text-slate-900" : "text-slate-500"}`}>
                          {s.label}
                        </p>
                      </div>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="flex-1 h-px mx-3 bg-gradient-to-r from-gray-200 to-gray-100">
                        <div
                          className="h-px bg-[#1e6091] transition-all duration-500"
                          style={{ width: s.done ? "100%" : "0%" }}
                        ></div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <form className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* ── Left column: Personal + Identity (2/3) ── */}
              <div className="lg:col-span-2 space-y-5">
                {/* Personal Information card */}
                <section className="rounded-2xl border border-[#1e6091]/30 bg-white p-6">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-5">
                    <i className="fa fa-user text-[#1e6091]"></i> Personal Information
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {[
                      { id: "fullName", label: "Full Name", icon: "fa-user", type: "text", value: fullName, ph: "John Doe", full: true },
                      { id: "email", label: "Email Address", icon: "fa-envelope", type: "email", value: email, ph: "your@email.com" },
                      { id: "mobile", label: "Mobile Number", icon: "fa-phone", type: "tel", value: mobile, ph: "9876543210" },
                    ].map((f) => (
                      <div key={f.id} className={f.full ? "sm:col-span-2" : ""}>
                        <label className="text-[11px] font-semibold text-slate-500 mb-1.5 block">
                          {f.label} <span className="text-rose-400">*</span>
                        </label>
                        <div className="relative flex items-center">
                          <span className="absolute left-3.5 text-slate-500">
                            <i className={`fa ${f.icon}`}></i>
                          </span>
                          <input
                            type={f.type}
                            value={f.value}
                            onChange={(e) => handleFieldChange(f.id, e.target.value)}
                            onBlur={(e) => handleFieldBlur(f.id, e.target.value)}
                            placeholder={f.ph}
                            className={`w-full pl-10 pr-9 py-3 bg-gray-50 border rounded-xl text-slate-900 placeholder:text-slate-500 focus:ring-2 transition-all outline-none ${fieldStateClass(f.id)}`}
                          />
                          {touched[f.id] && !errors[f.id] && (
                            <i className="fa fa-circle-check absolute right-3.5 text-emerald-500"></i>
                          )}
                          {touched[f.id] && errors[f.id] && (
                            <i className="fa fa-circle-exclamation absolute right-3.5 text-rose-400"></i>
                          )}
                        </div>
                        {touched[f.id] && errors[f.id] && (
                          <p className="mt-1.5 text-xs font-medium text-rose-500 flex items-center gap-1.5">
                            <i className="fa fa-circle-exclamation"></i>
                            {errors[f.id]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Identity Document card */}
                <section className="rounded-2xl border border-[#1e6091]/30 bg-white p-6">
                  <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900 mb-5">
                    <i className="fa fa-id-card text-[#1e6091]"></i> Identity Document
                  </h3>

                  <div className="mb-4">
                    <label className="text-[11px] font-semibold text-slate-500 mb-1.5 block">
                      PAN Card Number <span className="text-rose-400">*</span>
                    </label>
                    <div className="relative flex items-center">
                      <span className="absolute left-3.5 text-slate-500">
                        <i className="fa fa-id-card"></i>
                      </span>
                      <input
                        type="text"
                        value={panCard}
                        onChange={(e) => handleFieldChange("panCard", e.target.value)}
                        onBlur={(e) => handleFieldBlur("panCard", e.target.value)}
                        placeholder="ABCDE1234F"
                        maxLength={10}
                        className={`w-full pl-10 pr-9 py-3 bg-gray-50 border rounded-xl text-slate-900 tracking-[0.2em] placeholder:tracking-normal placeholder:text-slate-500 focus:ring-2 transition-all outline-none ${fieldStateClass("panCard")}`}
                      />
                      {touched.panCard && !errors.panCard && (
                        <i className="fa fa-circle-check absolute right-3.5 text-emerald-500"></i>
                      )}
                      {touched.panCard && errors.panCard && (
                        <i className="fa fa-circle-exclamation absolute right-3.5 text-rose-400"></i>
                      )}
                    </div>
                    {touched.panCard && errors.panCard && (
                      <p className="mt-1.5 text-xs font-medium text-rose-500 flex items-center gap-1.5">
                        <i className="fa fa-circle-exclamation"></i>
                        {errors.panCard}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-[11px] font-semibold text-slate-500 mb-1.5 block">
                      Upload PAN Card <span className="text-rose-400">*</span>
                    </label>
                    <label
                      className={`group flex flex-col items-center justify-center gap-2 w-full rounded-xl border-2 border-dashed bg-gray-50 px-4 py-7 text-center cursor-pointer transition-all ${
                        panCardImageError
                          ? "border-rose-400 hover:border-rose-500"
                          : panCardImage
                          ? "border-emerald-400 hover:border-emerald-500 bg-emerald-50/40"
                          : "border-[#1e6091]/40 hover:border-[#1e6091]/60 hover:bg-[#1e6091]/5"
                      }`}
                    >
                      <span
                        className={`grid place-items-center w-11 h-11 rounded-xl group-hover:scale-105 transition-transform ${
                          panCardImage ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-[#1e6091]"
                        }`}
                      >
                        <i className={`fa ${panCardImage ? "fa-circle-check" : "fa-cloud-arrow-up"} text-lg`}></i>
                      </span>
                      <span className="text-sm font-medium text-slate-600">
                        <span className="text-[#1e6091]">Click to upload</span> PAN card
                      </span>
                      <span className="text-[11px] text-slate-500">PNG, JPG or PDF · max 5MB</span>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,application/pdf"
                        onChange={handlePanCardImageChange}
                        className="hidden"
                      />
                    </label>

                    {panCardImage && (
                      <div className="mt-3 flex items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3">
                        <span className="grid place-items-center w-9 h-9 rounded-lg bg-emerald-100 text-emerald-600 shrink-0">
                          <i className={`fa ${panCardImage.type === "application/pdf" ? "fa-file-pdf" : "fa-file-image"}`}></i>
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{panCardImage.name}</p>
                          <p className="text-[11px] text-emerald-600">Uploaded successfully</p>
                        </div>
                        <i className="fa fa-circle-check text-emerald-500"></i>
                      </div>
                    )}

                    {panCardImagePreview && panCardImage?.type.startsWith("image/") && (
                      <div className="mt-3">
                        <img
                          src={panCardImagePreview}
                          alt="PAN card preview"
                          className="max-h-52 w-full rounded-xl border border-[#1e6091]/30 object-contain bg-gray-50"
                        />
                      </div>
                    )}

                    {panCardImageError && (
                      <p className="mt-2 text-xs font-medium text-rose-500 flex items-center gap-1.5">
                        <i className="fa fa-circle-exclamation"></i>
                        {panCardImageError}
                      </p>
                    )}
                  </div>
                </section>
              </div>

              {/* ── Right column: Signature + Submit (1/3) ── */}
              <div className="lg:col-span-1 space-y-5">
                <section className="rounded-2xl border border-[#1e6091]/30 bg-white p-6">
                  <h3 className="flex items-center justify-between text-sm font-bold text-slate-900 mb-5">
                    <span className="flex items-center gap-2">
                      <i className="fa fa-pen-nib text-[#1e6091]"></i> E-Signature <span className="text-rose-400">*</span>
                    </span>
                    {isSaved && (
                      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                        <i className="fa fa-check"></i> Captured
                      </span>
                    )}
                  </h3>

                  <div
                    className={`relative rounded-xl border bg-white p-2 ${
                      signatureError ? "border-rose-400" : isSaved ? "border-emerald-400" : "border-[#1e6091]/30"
                    }`}
                  >
                    <SignatureCanvas
                      penColor="#0f172a"
                      canvasProps={{ className: "w-full h-40 rounded-lg bg-white" }}
                      onEnd={() => {
                        setSignature(sigPadRef.current?.toDataURL() ?? null);
                        setSignatureError("");
                      }}
                      ref={sigPadRef}
                    />
                    {!signature && (
                      <span className="pointer-events-none absolute inset-0 grid place-items-center text-xs text-slate-500">
                        <span className="flex items-center gap-2">
                          <i className="fa fa-pen-nib"></i> Sign here
                        </span>
                      </span>
                    )}
                  </div>

                  {signatureError && (
                    <p className="mt-2 text-xs font-medium text-rose-500 flex items-center gap-1.5">
                      <i className="fa fa-circle-exclamation"></i>
                      {signatureError}
                    </p>
                  )}

                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border border-[#1e6091]/40 text-slate-600 hover:bg-gray-100 transition-colors"
                    >
                      <i className="fa fa-eraser"></i> Clear
                    </button>
                    <button
                      type="button"
                      onClick={saveSignature}
                      className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
                        isSaved
                          ? "bg-emerald-500 text-white"
                          : "bg-gray-100 text-slate-900 hover:bg-gray-200"
                      }`}
                    >
                      <i className={`fa ${isSaved ? "fa-check" : "fa-floppy-disk"}`}></i>
                      {isSaved ? "Saved" : "Save"}
                    </button>
                  </div>
                </section>

                {/* Submit card */}
                <section className="rounded-2xl border border-[#1e6091]/30 bg-white p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-slate-500">Completion</span>
                    <span className="text-xs font-bold text-[#1e6091]">{progress}%</span>
                  </div>
                  <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden mb-5">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#1e6091] to-[#3d8bc0] transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>

                  {!canSubmit && Object.values(touched).some(Boolean) && (
                    <p className="mb-3 text-xs font-medium text-rose-500 flex items-center gap-1.5">
                      <i className="fa fa-circle-exclamation"></i>
                      Please complete all required fields correctly before submitting.
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-3 bg-[#1e6091] text-white py-3.5 rounded-xl font-bold text-base shadow-lg shadow-[#1e6091]/20 hover:bg-[#185076] active:scale-[0.99] transition-all disabled:opacity-30 disabled:shadow-none disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Processing…
                      </>
                    ) : (
                      <>
                        <i className="fa fa-lock"></i> Submit & Activate
                      </>
                    )}
                  </button>

                  <p className="mt-3 text-center text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
                    <i className="fa fa-shield-halved text-[#1e6091]"></i>
                    Bank-grade encryption · SEBI compliant
                  </p>
                </section>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}